'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { BellOff, Mic2, Video, MoreHorizontal, X, ExternalLink, Check } from 'lucide-react'
import { setSubscriptionType, unsubscribe, type Subscription } from '@/lib/subscriptions'
import { cx } from '@/components/ui/kit'

/**
 * Avatars ronds des abonnements (artistes / chaînes) :
 *  - clic = filtre le fil sur cet abonnement (re-clic = retire le filtre)
 *  - bouton « ⋯ » au survol, clic droit ou appui long = feuille d'actions
 *    (voir la chaîne, reclasser artiste ↔ chaîne, se désabonner).
 */

/** Pastille ronde (image ou initiale sur dégradé d'accent). */
export function SubAvatarImg({ sub, size = 88, className = '' }: { sub: Subscription; size?: number; className?: string }) {
  const [broken, setBroken] = useState(false)
  const initial = (sub.title || '?').replace(/^\W+/, '').slice(0, 1).toUpperCase() || '?'
  return (
    <span style={{ width: size, height: size }} className={cx('relative block rounded-full overflow-hidden shrink-0 bg-spanel3', className)}>
      {sub.thumbnail && !broken
        ? <img src={sub.thumbnail} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setBroken(true)} className="absolute inset-0 w-full h-full object-cover" />
        : <span className="on-media absolute inset-0 grid place-items-center bg-gradient-to-br from-a2 to-adeep font-extrabold text-white" style={{ fontSize: Math.round(size * 0.38) }}>{initial}</span>}
    </span>
  )
}

function TypeBadge({ type }: { type: Subscription['type'] }) {
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[10.5px] font-bold uppercase tracking-wide',
      type === 'artist' ? 'bg-a2/20 text-a4' : 'bg-white/[0.08] text-white/65')}>
      {type === 'artist' ? <Mic2 size={10} /> : <Video size={10} />}
      {type === 'artist' ? 'Artiste' : 'Chaîne'}
    </span>
  )
}

/** Un abonnement : avatar + nom + badge. */
export function SubAvatar({ sub, selected, dimmed, onSelect, onMenu, compact = false }: {
  sub: Subscription; selected: boolean; dimmed: boolean; onSelect: () => void; onMenu: () => void; compact?: boolean
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressed = useRef(false)
  const size = compact ? 76 : 88
  return (
    <div className={cx('group relative flex flex-col items-center text-center shrink-0 transition-opacity', compact ? 'w-[92px]' : 'w-[96px] md:w-[108px]', dimmed && 'opacity-45 hover:opacity-100')}>
      <button type="button"
        onClick={() => { if (longPressed.current) { longPressed.current = false; return } onSelect() }}
        onContextMenu={e => { e.preventDefault(); onMenu() }}
        onTouchStart={() => {
          longPressed.current = false
          if (timer.current) clearTimeout(timer.current)
          timer.current = setTimeout(() => { longPressed.current = true; try { navigator.vibrate?.(18) } catch {}; onMenu() }, 480)
        }}
        onTouchMove={() => { if (timer.current) clearTimeout(timer.current) }}
        onTouchEnd={() => { if (timer.current) clearTimeout(timer.current) }}
        aria-pressed={selected}
        title={selected ? `Retirer le filtre « ${sub.title} »` : `Voir les nouveautés de ${sub.title}`}
        className="flex flex-col items-center w-full">
        <span className={cx('relative rounded-full p-[3px] transition',
          selected ? 'bg-gradient-to-br from-a3 via-a2 to-a1 shadow-[0_0_24px_rgb(var(--a2)/.6)] scale-[1.04]'
                   : 'bg-white/[0.08] group-hover:bg-gradient-to-br group-hover:from-a3 group-hover:to-a1')}>
          <span className="block rounded-full p-[2px] bg-sbg">
            <SubAvatarImg sub={sub} size={size} />
          </span>
          {selected && (
            <span className="absolute bottom-0.5 right-0.5 w-6 h-6 rounded-full grid place-items-center bg-a2 text-white ring-2 ring-sbg">
              <Check size={13} strokeWidth={3} />
            </span>
          )}
        </span>
        <span className={cx('mt-2 w-full text-[13.5px] font-semibold leading-tight line-clamp-2 break-words', selected ? 'text-a4' : 'text-white/90')}>
          {sub.title || 'Chaîne'}
        </span>
      </button>
      <span className="mt-1"><TypeBadge type={sub.type} /></span>
      {/* Menu d'actions : visible au survol (desktop), toujours sur écran tactile. */}
      <button type="button" onClick={onMenu} title="Options" aria-label={`Options pour ${sub.title}`}
        className="absolute top-0 right-0 md:right-1 w-8 h-8 rounded-full grid place-items-center bg-spanel2 border border-white/10 text-white/80 hover:text-white hover:border-a2/60 shadow-lg transition opacity-100 md:opacity-0 group-hover:opacity-100 focus:opacity-100">
        <MoreHorizontal size={16} />
      </button>
    </div>
  )
}

/** Feuille d'actions d'un abonnement (bas d'écran sur mobile, centrée sur desktop). */
export function SubActionSheet({ sub, onClose }: { sub: Subscription; onClose: () => void }) {
  const isArtist = sub.type === 'artist'
  const item = 'w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left text-[15px] font-semibold transition'
  return (
    <div onClick={onClose} className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4">
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label={`Options pour ${sub.title}`}
        className="w-full max-w-sm rounded-3xl bg-spanel border border-white/[0.09] shadow-[0_24px_60px_rgba(0,0,0,.6)] p-3 relative">
        <button onClick={onClose} title="Fermer" aria-label="Fermer" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.07] hover:bg-white/[0.14] grid place-items-center text-white/70"><X size={16} /></button>
        <div className="flex items-center gap-3 p-2 pr-10 mb-2">
          <SubAvatarImg sub={sub} size={56} />
          <div className="min-w-0">
            <div className="text-[16px] font-bold truncate">{sub.title || 'Chaîne'}</div>
            <div className="mt-1"><TypeBadge type={sub.type} /></div>
          </div>
        </div>
        <Link href={`/app/video/channel/${sub.channelId}`} onClick={onClose} className={cx(item, 'text-white/90 hover:bg-white/[0.06]')}>
          <ExternalLink size={18} className="text-a4" /> Voir la chaîne
        </Link>
        <button type="button" onClick={() => { setSubscriptionType(sub.channelId, isArtist ? 'channel' : 'artist'); onClose() }}
          title={isArtist ? 'Reclasser comme chaîne (vidéos)' : 'Marquer comme artiste (nouveautés musique)'}
          className={cx(item, 'text-white/90 hover:bg-white/[0.06]')}>
          {isArtist ? <Video size={18} className="text-a4" /> : <Mic2 size={18} className="text-a4" />}
          {isArtist ? 'Reclasser comme chaîne' : 'Marquer comme artiste'}
        </button>
        <button type="button" onClick={() => { unsubscribe(sub.channelId); onClose() }}
          className={cx(item, 'text-rose-300 hover:bg-rose-500/10')}>
          <BellOff size={18} /> Se désabonner
        </button>
      </div>
    </div>
  )
}
